import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { X, Play } from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout, isAuthenticated, isDemoMode } = useAuth();

  const menuItems = [
    { name: 'Dashboard', path: '/', icon: '📊' },
    { name: 'Reviews', path: '/reviews', icon: '💬' },
    { name: 'Locations', path: '/locations', icon: '📍' },
    { name: 'Analytics', path: '/analytics', icon: '📈' },
    { name: 'Subscription', path: '/subscription', icon: '💎' },
    { name: 'Settings', path: '/settings', icon: '⚙️' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getUserInitials = () => {
    if (!user?.name) return '?';
    const names = user.name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserAvatar = () => {
    return user?.picture || null;
  };

  const handleLinkClick = () => {
    // Close sidebar on mobile when clicking a link
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <div className={`
      w-64 flex-shrink-0 flex flex-col bg-[#2C3E50] text-white shadow-xl
      fixed lg:relative h-screen z-50 transition-transform duration-300 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    `}>
      {/* Close button for mobile */}
      <button
        onClick={onClose}
        className="lg:hidden absolute top-4 right-4 text-white hover:bg-white/10 rounded-lg p-2"
      >
        <X className="w-6 h-6" />
      </button>

      {/* User Profile Section */}
      <div className="p-6 border-b border-white/10">
        {isAuthenticated ? (
          <div className="flex items-center gap-3">
            {isDemoMode ? (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Play className="w-6 h-6 text-white" />
              </div>
            ) : getUserAvatar() ? (
              <img 
                src={getUserAvatar()} 
                alt="Profile" 
                className="w-12 h-12 rounded-full border-2 border-blue-400"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center font-bold text-white shadow-lg">
                {getUserInitials()}
              </div>
            )}
            <div>
              <div className="font-semibold text-white text-sm">
                {isDemoMode ? 'Demo Mode' : (user?.name || 'User')}
              </div>
              <div className="text-xs text-slate-300">
                {isDemoMode ? 'Testing with mock data' : 'Business Owner'}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-600/50 flex items-center justify-center font-bold text-slate-400">
              ?
            </div>
            <div>
              <div className="font-semibold text-slate-400 text-sm">Not Connected</div>
              <div className="text-xs text-slate-500">Connect to start</div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={handleLinkClick}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              isActive(item.path)
                ? 'bg-blue-600/20 text-white border-l-4 border-blue-500 shadow-sm'
                : 'text-slate-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            <span className="text-lg w-5 flex items-center justify-center">{item.icon}</span>
            <span className="font-medium text-sm">{item.name}</span>
          </Link>
        ))}
      </nav>

      {/* Logout Button */}
      <div className="p-4 border-t border-white/10">
        <button
          onClick={() => {
            logout();
            handleLinkClick();
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-600/10 hover:text-red-400 transition-all duration-200"
        >
          <span className="text-lg w-5 flex items-center justify-center">🚪</span>
          <span className="font-medium text-sm">{isDemoMode ? 'Exit Demo' : 'Log Out'}</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
